import { useEffect } from "react";
import { Form, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import CustomModal from "../../components/customModal";
import useUserSettings from "../hooks/useUserSettings";
import type { AppDispatch, RootState } from "../../store";
import {
    setAutoPrintState,
    setPrintMethodState,
    setPrinterSizeState,
    setSelectAllPayablesState,
    setShowKeyboardState,
    toggleShowSettingsModal,
} from "../../store/slices/appGeneralSlice";
// import packageJson from "../../package.json";

const PRINT_METHODS: ReadonlyArray<PrintMethod> = ["browser", "bluetooth", "usb", "external"];


const AppSettingsModal = () => {
    const { t } = useTranslation();

    const dispatch = useDispatch<AppDispatch>();

    // const appVersion = packageJson.version;
    const appVersion = process.env.REACT_APP_VERSION;

    const showModal = useSelector((state: RootState) => state.appGeneral.showSettingsModal);
    const printMethod = useSelector((state: RootState) => state.appGeneral.printMethod);
    const autoPrint = useSelector((state: RootState) => state.appGeneral.autoPrint);
    const printerSize = useSelector((state: RootState) => state.appGeneral.printerSize);
    const selectAllPayablesByDefault = useSelector((state: RootState) => state.appGeneral.selectAllPayablesByDefault);
    const showKeyboard = useSelector((state: RootState) => state.appGeneral.showKeyboard);

    const isBrowserPrintMethod = printMethod === "browser";
    const effectiveAutoPrint = !isBrowserPrintMethod && autoPrint;

    const { userSettings: userSavedSettings = {}, saveUserSettings } = useUserSettings();

    const handleCloseModal = () => {
        dispatch(toggleShowSettingsModal({ showModal: false }));
    };

    const changePrintMethod = (nextPrintMethod: PrintMethod) => {
        dispatch(setPrintMethodState({ printMethod: nextPrintMethod }));
        saveUserSettings({
            ...userSavedSettings,
            settings: {
                ...userSavedSettings?.settings,
                print: {
                    ...userSavedSettings?.settings?.print,
                    printMethod: nextPrintMethod,
                },
            },
        });
    };

    const changeAutoPrintState = (nextAutoPrint: boolean) => {
        dispatch(setAutoPrintState({ autoPrint: nextAutoPrint }));
        saveUserSettings({
            ...userSavedSettings,
            settings: {
                ...userSavedSettings?.settings,
                print: {
                    ...userSavedSettings?.settings?.print,
                    autoPrint: nextAutoPrint,
                },
            },
        });
    };

    const changePrinterSize = (size: AppGeneralState["printerSize"]) => {
        dispatch(setPrinterSizeState({ printerSize: size }));
        saveUserSettings({
            ...userSavedSettings,
            settings: {
                ...userSavedSettings?.settings,
                print: {
                    ...userSavedSettings?.settings?.print,
                    printerSize: size,
                },
            },
        });
    };

    const changeSelectAllPayablesByDefaultState = (selectAllByDefault: boolean) => {
        dispatch(setSelectAllPayablesState({ selectAllPayablesByDefault: selectAllByDefault }));
        saveUserSettings({
            ...userSavedSettings,
            settings: {
                ...userSavedSettings?.settings,
                payment: {
                    ...userSavedSettings?.settings?.payment,
                    selectAllPayablesByDefault: selectAllByDefault,
                },
            },
        });
    };

    const changeShowKeyboardState = (nextShowKeyboard: boolean) => {
        dispatch(setShowKeyboardState({ showKeyboard: nextShowKeyboard }));
        saveUserSettings({
            ...userSavedSettings,
            settings: {
                ...userSavedSettings?.settings,
                ui: {
                    ...userSavedSettings?.settings?.ui,
                    showKeyboard: nextShowKeyboard,
                },
            },
        });
    };

    useEffect(() => {
        /**
         * Printing
         */
        const oldGeneralOptions = userSavedSettings?.generalOptions; // Get previous versions data (was contain 'useAppPrinter', 'useBluetoothPrinter', 'printerSize' and 'selectAllPayablesByDefault')
        const savedPrintSettings = userSavedSettings?.settings?.print || {};
        const savedPaymentSettings = userSavedSettings?.settings?.payment || {};
        const savedUiSettings = userSavedSettings?.settings?.ui || {};

        const {
            useAppPrinter: useAppPrinterInLS,
            useBluetoothPrinter: useBluetoothPrinterInLS,
        } = oldGeneralOptions || {};

        const {
            // useAppPrinter: useAppPrinterInLS,
            // useBluetoothPrinter: useBluetoothPrinterInLS,
            printerSize: printerSizeInLS,
            autoPrint: autoPrintInLS,
        } = oldGeneralOptions || savedPrintSettings;

        const savedPrintMethod = PRINT_METHODS.includes(savedPrintSettings?.printMethod as PrintMethod)
            ? (savedPrintSettings.printMethod as PrintMethod)
            : undefined;

        const migratedPrintMethod =
            savedPrintMethod ||
            (useAppPrinterInLS
                ? "external" :
            useBluetoothPrinterInLS
                ? "bluetooth"
                : undefined);

        if (migratedPrintMethod && migratedPrintMethod !== printMethod) {
            dispatch(setPrintMethodState({ printMethod: migratedPrintMethod }));
        }
        if (autoPrintInLS !== undefined && autoPrintInLS !== autoPrint) {
            dispatch(setAutoPrintState({ autoPrint: autoPrintInLS }));
        }
        if (printerSizeInLS !== undefined && printerSizeInLS !== printerSize) {
            dispatch(setPrinterSizeState({ printerSize: printerSizeInLS }));
        }

        /**
         * Payment
         */
        const {
            selectAllPayablesByDefault: selectAllPayablesByDefaultInLS,
        } = oldGeneralOptions || savedPaymentSettings;

        if (selectAllPayablesByDefaultInLS !== undefined && selectAllPayablesByDefaultInLS !== selectAllPayablesByDefault) {
            dispatch(setSelectAllPayablesState({ selectAllPayablesByDefault: selectAllPayablesByDefaultInLS }));
        }

        /**
         * UI
         */
        const {
            showKeyboard: showKeyboardInLS,
        } = savedUiSettings;

        if (showKeyboardInLS !== undefined && showKeyboardInLS !== showKeyboard) {
            dispatch(setShowKeyboardState({ showKeyboard: showKeyboardInLS }));
        }
    }, [dispatch, userSavedSettings, printMethod, autoPrint, printerSize, selectAllPayablesByDefault, showKeyboard]);

    return (
        <CustomModal
            // size="md"
            show={showModal}
            onHide={handleCloseModal}
            centered
            className="profile-settings-modal"
        >
            <Modal.Header closeButton>
                <span className="fs-5 fw-medium">
                    <span className="icon me-2 fs-3">
                        <svg stroke="currentColor" fill="none" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
                    </span>
                    {t("settings")}
                </span>
            </Modal.Header>
            <Modal.Body>
                <div className="px-3">
                    <div className="settings-subgroup-title fw-lighter mb-3">
                        {t("generalSettings")}
                    </div>
                    <ul className="settings-subgroup-options px-2">
                        <li className="settings-subgroup-option">
                            <label className="d-inline-block">
                                <div className="item-title">
                                    {t("printMethod")}
                                </div>
                                <div className="item-description">
                                    {t("singlePrintMethodDescription")}
                                </div>
                            </label>
                        </li>

                        <li className="settings-subgroup-option ps-4">
                            <div className="d-flex flex-column gap-3 pe-3 w-100">
                                <div>
                                    <Form.Check
                                        type="radio"
                                        id="printMethod_browser"
                                        name="print_method"
                                        label={t("printMethodBrowser")}
                                        checked={printMethod === "browser"}
                                        onChange={() => changePrintMethod("browser")}
                                    />
                                </div>
                                <div>
                                    <Form.Check
                                        type="radio"
                                        id="printMethod_bluetooth"
                                        name="print_method"
                                        label={t("printMethodBluetooth")}
                                        checked={printMethod === "bluetooth"}
                                        onChange={() => changePrintMethod("bluetooth")}
                                    />
                                    <div className="text-black-50 text-muted ps-4">
                                        {t("bluetoothPrinterDetails")}
                                    </div>
                                </div>
                                <div>
                                    <Form.Check
                                        type="radio"
                                        id="printMethod_usb"
                                        name="print_method"
                                        label={t("printMethodUsb")}
                                        checked={printMethod === "usb"}
                                        onChange={() => changePrintMethod("usb")}
                                    />
                                    <div className="text-black-50 text-muted ps-4">
                                        {t("usbPrinterDetails")}
                                    </div>
                                </div>
                                <div>
                                    <Form.Check
                                        type="radio"
                                        id="printMethod_external"
                                        name="print_method"
                                        label={t("printMethodExternal")}
                                        checked={printMethod === "external"}
                                        onChange={() => changePrintMethod("external")}
                                    />
                                    <div className="text-black-50 text-muted ps-4">
                                        {t("externalPrinterDetails")}
                                    </div>
                                </div>
                            </div>
                        </li>

                        {(printMethod === "bluetooth" || printMethod === "usb") && (
                            <li className="settings-subgroup-option ps-4 mt-5">
                                <label className="d-inline-block pe-3">
                                    <div className="item-title">
                                        {t("printerSize")}
                                    </div>
                                </label>
                                <div className="d-flex align-items-center gap-3 pe-3 fs-5">
                                    <Form.Check
                                        type="radio"
                                        id="printerSize_58_radio"
                                        className="d-inline-block"
                                        label={
                                            <label className="mb-0" htmlFor="printerSize_58_radio">
                                                58mm
                                            </label>
                                        }
                                        value="58mm"
                                        checked={printerSize === "58mm"}
                                        onChange={() => changePrinterSize("58mm")}
                                    />
                                    <Form.Check
                                        type="radio"
                                        id="printerSize_80_radio"
                                        className="d-inline-block"
                                        label={
                                            <label className="mb-0" htmlFor="printerSize_80_radio">
                                                80mm
                                            </label>
                                        }
                                        value="80mm"
                                        checked={printerSize === "80mm"}
                                        onChange={() => changePrinterSize("80mm")}
                                    />
                                </div>
                            </li>
                        )}

                        <li className="flex-column mt-5">
                            <div className="flex-grow-1 d-flex justify-content-between align-items-center gap-2">
                                <label className="d-inline-block" htmlFor="autoPrint_switch">
                                    <div className="item-title">
                                        {t("autoPrint")}
                                    </div>
                                    <div className="item-description">
                                        {isBrowserPrintMethod
                                            ? t("autoPrintUnavailableForBrowserDescription")
                                            : t("autoPrintDescription")}
                                    </div>
                                </label>
                                <span className="fs-4">
                                    <Form.Check
                                        type="switch"
                                        id="autoPrint_switch"
                                        className="d-inline-block"
                                        label=""
                                        onChange={() => !isBrowserPrintMethod && changeAutoPrintState(!autoPrint)}
                                        checked={effectiveAutoPrint}
                                        disabled={isBrowserPrintMethod}
                                    />
                                </span>
                            </div>
                            {isBrowserPrintMethod && (
                                <div className="text-black-50 text-muted pe-3">
                                    {t("browserPrintMethodNotice")}
                                </div>
                            )}
                        </li>

                        <li className="settings-subgroup-option mt-5">
                            <label className="d-inline-block" htmlFor="selectAllPayablesByDefault_switch">
                                <div className="item-title">
                                    {t("selectAllPayablesByDefault")}
                                </div>
                                <div className="item-description">
                                    {t("selectAllPayablesByDefaultOnThePaymentPage")}
                                </div>
                            </label>
                            <span className="fs-4">
                                <Form.Check
                                    type="switch"
                                    id="selectAllPayablesByDefault_switch"
                                    className="d-inline-block"
                                    label=""
                                    onChange={() => changeSelectAllPayablesByDefaultState(!selectAllPayablesByDefault)}
                                    checked={selectAllPayablesByDefault}
                                />
                            </span>
                        </li>

                        <li className="settings-subgroup-option mt-5">
                            <label className="d-inline-block" htmlFor="showKeyboard_switch">
                                <div className="item-title">
                                    {t("virtualKeyboard")}
                                </div>
                                <div className="item-description">
                                    {t("virtualKeyboardDescription")}
                                </div>
                            </label>
                            <span className="fs-4">
                                <Form.Check
                                    type="switch"
                                    id="showKeyboard_switch"
                                    className="d-inline-block"
                                    label=""
                                    onChange={() => changeShowKeyboardState(!showKeyboard)}
                                    checked={showKeyboard}
                                />
                            </span>
                        </li>
                    </ul>

                    {appVersion && (
                        <div className="app-version-label text-end text-muted mt-5">
                            v{appVersion}
                        </div>
                    )}
                </div>
            </Modal.Body>
        </CustomModal>
    );
};

export default AppSettingsModal;
